import {
  type ArtAsset,
  RACES,
  type Race,
  RESEARCH_DOMAINS,
  type ResearchDomainId,
  type ResearchPrice,
  researchTrees,
} from "@sins2-atlas/game-data";
import { dataset } from "./dataset";
import { type FactionSelection, racesOf } from "./faction-selection";
import {
  buildResearchIndex,
  type IndexedSubject,
  parseKey,
  type ResearchIndex,
  subjectKey,
  type TechKey,
  type TechSet,
  tierKey,
} from "./tech-set";
import { exclusiveFactionOf } from "./vocabulary";

// The dataset's side of the Tech Set (CONTEXT.md: Tech Set; ADR 0009). tech-set.ts holds the
// closure rules and the URL grammar over an abstract ResearchIndex; this module builds that
// index out of the shipped dataset and derives what the tick list may offer. Nothing here is
// hand-written vocabulary: a patch that adds a Research Subject to a hull bonus list adds it
// to the tick list, and one that drops it drops it, with no code edit.

/** The six trees' 849 subjects as the closure's index — every subject, so a prerequisite
 * chain never dead-ends on a subject the picker happens not to offer. */
export const researchIndex: ResearchIndex = buildResearchIndex(
  researchTrees(dataset.research).flatMap(({ race, domain, tree }) =>
    tree.subjects.map(
      (subject): IndexedSubject => ({
        id: subject.id,
        name: subject.name,
        race,
        domain,
        tier: subject.tier,
        prerequisites: subject.prerequisites,
      }),
    ),
  ),
);

// What a Tech Set may hold, per Race: EVERY Research Subject in the Race's two trees, plus
// every Domain Tier unlock. A Tech Set is the research the reader has done, not the research
// this site happens to draw a number for (ADR 0009 amendment, user decision 2026-09-06).
//
// Until then the rule was "a subject may be ticked only when ticking it could move a published
// cell", derived from the union of every research source the Race's units' tagged lists name.
// It was defensible and it read as a bug: it offered 120 of the game's 849 subjects and NONE of
// the four Civilian trees, so on a Civilian tree the five Tier buttons said "Add to set" and
// all hundred subjects said "this changes no stat this site publishes". A control that works on
// one cell and not the one beside it teaches nothing about the game; it teaches the reader that
// the site is unreliable. A tick that moves no number is now simply a tick that moves no number
// — the row shows the subject's own Effect Lines, so what it does is on the row.
//
// This also makes Issue 39's silent members impossible by construction: every member of a shown
// Race's set has a row, so the trigger's count and the panel's checked rows agree without a
// second mechanism.
const KEYS_BY_RACE: ReadonlyMap<Race, ReadonlySet<TechKey>> = (() => {
  const byRace = new Map<Race, Set<TechKey>>(RACES.map((race) => [race, new Set<TechKey>()]));
  for (const { race, domain, tree } of researchTrees(dataset.research)) {
    const keys = byRace.get(race);
    if (keys === undefined) continue;
    for (const subject of tree.subjects) keys.add(subjectKey(subject.id));
    for (let tier = 0; tier < tree.tiers.length; tier += 1) keys.add(tierKey(race, domain, tier));
  }
  return byRace;
})();

/** Every key one Race's Tech Set may hold: its subjects and its ten Tier unlocks. */
export function tickableKeys(race: Race): ReadonlySet<TechKey> {
  return KEYS_BY_RACE.get(race) ?? new Set<TechKey>();
}

/** The Races a Tech Set names: those whose own trees hold at least one of its keys, in the
 * dataset's Race order. A set is race-blind by design — it is the research the reader has
 * done, and one URL can carry two empires' — so this is the question every surface that draws
 * the picker must ask of the set before it decides which sections to draw. */
export function racesOfTechSet(set: TechSet | undefined): Race[] {
  if (set === undefined || set.size === 0) return [];
  return RACES.filter((race) => {
    const keys = tickableKeys(race);
    for (const key of set) if (keys.has(key)) return true;
    return false;
  });
}

/** One key's display name in the game's own words: a Research Subject's name, or a Tier's
 * "<Domain name> <numeral>". It reads the trees rather than the tick list because the thing
 * that requires a row may be one the picker does not offer — a prerequisite that moves no
 * published stat is still the honest answer to "why is this ticked". */
export function techKeyLabel(key: TechKey): string | undefined {
  const parsed = parseKey(key);
  if (parsed === undefined) return undefined;
  if (parsed.kind === "tier") return tierName(parsed.race, parsed.domain, parsed.tier);
  return SUBJECTS.get(parsed.id)?.subject.name;
}

/** One tickable row: a Research Subject or a Domain Tier unlock, under the game's own name and
 * carrying what the game's own tree tooltip carries for it (Issue 25) — so a reader ticking a
 * name can read what it does without leaving the panel. */
export interface TechOption {
  key: TechKey;
  /** The game's own display name: a subject's name, or "<Domain name> <numeral>" for a Tier. */
  label: string;
  kind: "subject" | "tier";
  domain: ResearchDomainId;
  tier: number;
  /** The game-worded Effect Lines this row grants: a subject's own composed lines, a Tier's
   * Tier Bonuses. Empty is honest absence — an unlock-style subject composes no subject-side
   * line, and the panel says nothing rather than inventing one. */
  effects: readonly string[];
  /** The game's own Field name (the tree's band label, shipped uppercase) on a subject row.
   * A Tier belongs to no Field: it is the whole Domain's stage. */
  field?: string;
  /** The game's own Price, the same figure /tech's tooltip shows. */
  price: ResearchPrice;
  /** The art the tree draws for the row: a subject's hud icon, a Tier's button background. */
  art: ArtAsset;
  /** The short name of the one Faction of the Race that can research the subject, where the
   * Race has another Faction to exclude (vocabulary.ts holds the rule). */
  faction?: string;
}

export interface RaceTechOptions {
  race: Race;
  options: readonly TechOption[];
}

/** One Tier of one Domain as the picker groups it: the Tier's own unlock row, then the
 * subjects that sit in it, in tree order. */
interface TechTierGroup {
  tier: number;
  /** The game's own Tier name: "<Domain name> <numeral>". */
  name: string;
  unlock: TechOption;
  subjects: TechOption[];
}

/** One Domain of one Race, under the Race's own name for it (CONTEXT.md: Domain). */
interface TechDomainGroup {
  domain: ResearchDomainId;
  name: string;
  tiers: TechTierGroup[];
}

export interface RaceTechGroups {
  race: Race;
  domains: TechDomainGroup[];
}

/** Every Tier unlock of one Race: both Domains, all five Tiers, named as the game names
 * them (the Domain's own name and the game-wide tier numeral). All ten are offered, not only
 * the three whose Tier Bonuses reach a published cell: a Tier is what a subject's closure
 * requires, so a reader must see every one of them tick and untick. */
function tierOption(race: Race, domain: ResearchDomainId, tier: number): TechOption {
  const entry = dataset.research.races[race][domain].tiers[tier];
  if (entry === undefined) throw new Error(`no tier ${tier} in ${race}/${domain}`);
  return {
    key: tierKey(race, domain, tier),
    label: tierName(race, domain, tier),
    kind: "tier",
    domain,
    tier,
    effects: entry.bonuses,
    price: entry.price,
    art: entry.art.buttonBackground,
  };
}

function tierOptions(race: Race): TechOption[] {
  return RESEARCH_DOMAINS.flatMap((domain) =>
    dataset.research.races[race][domain].tiers.map((_, tier) => tierOption(race, domain, tier)),
  );
}

/** The game's own tier wording, "Military III": a Domain's name and the game-wide numeral
 * for the 0-based tier index. The one formula for it — the picker's rows and the Tech page's
 * tooltips, buttons and detail cards all read it (review 2026-09-08: the page had restated
 * it). */
export function tierLabel(domainName: string, tier: number): string {
  return `${domainName} ${dataset.research.tierNames[tier] ?? tier + 1}`;
}

/** The game's own name for one Tier: the Race's Domain name and the game-wide numeral. */
function tierName(race: Race, domain: ResearchDomainId, tier: number): string {
  return tierLabel(dataset.research.races[race][domain].name, tier);
}

/** Where a subject sits in the tree, for ordering within its Tier: the index of its Field
 * band (the game's own vertical order) and its grid slot inside that band. */
const SLOT = new Map<string, [number, number, number]>(
  researchTrees(dataset.research).flatMap(({ tree }) =>
    tree.subjects.map((subject): [string, [number, number, number]] => {
      const band = tree.fields.findIndex((field) => field.id === subject.field);
      const [column, row] = subject.coord;
      return [subject.id, [band, column, row]];
    }),
  ),
);

/** The dataset's own subject record, which the index deliberately does not carry (the closure
 * needs four fields; the picker's row needs the game's words). */
const SUBJECTS = new Map(
  researchTrees(dataset.research).flatMap(({ race, tree }) =>
    tree.subjects.map((subject) => [subject.id, { race, tree, subject }] as const),
  ),
);

/** A Research Subject's display name by id, across every Race and Domain — the join that
 * turns a prerequisite or requirement id ("trader_upgrade_consumables_0") into the tree's own
 * name, or undefined for an id no tree owns. Read off the one subject map above (review
 * 2026-09-08: lib/dataset.ts had walked the trees into a second map for the same names). */
export function researchSubjectName(id: string): string | undefined {
  return SUBJECTS.get(id)?.subject.name;
}

/** One Research Subject's row, in the game's own words — the same words and art the tree
 * draws for it, because it is the same subject. */
function subjectOption(key: TechKey, race: Race): TechOption | undefined {
  const parsed = parseKey(key);
  if (parsed?.kind !== "subject") return undefined;
  const found = SUBJECTS.get(parsed.id);
  if (found === undefined || found.race !== race) return undefined;
  const { subject, tree } = found;
  const faction = exclusiveFactionOf(race, subject.factions);
  return {
    key,
    label: subject.name,
    kind: "subject",
    domain: subject.domain,
    tier: subject.tier,
    effects: subject.effects,
    field: tree.fields.find((field) => field.id === subject.field)?.name,
    price: subject.price,
    art: subject.art.hudIcon,
    ...(faction === undefined ? {} : { faction: faction.short }),
  };
}

function subjectOptions(race: Race): TechOption[] {
  const options: TechOption[] = [];
  for (const key of tickableKeys(race)) {
    const option = subjectOption(key, race);
    if (option !== undefined) options.push(option);
  }
  return options;
}

/** Tree order between two subject rows of the same Tier: the Field band the game stacks them
 * in, then the grid slot inside it — the column the game draws them in (a Tier owns two), then
 * the row down that column. The slot alone cannot decide it: coordinates are unique per Field,
 * not per Domain, so two subjects of one Tier in different bands share a slot (TEC Military II
 * has exactly that pair). The key breaks a remaining tie so the order is total. */
function compareSlots(a: TechOption, b: TechOption): number {
  const left = SLOT.get(a.key.slice("subject:".length)) ?? [0, 0, 0];
  const right = SLOT.get(b.key.slice("subject:".length)) ?? [0, 0, 0];
  return left[0] - right[0] || left[1] - right[1] || left[2] - right[2];
}

/** The tick list for the Races present on the page, in the dataset's Race order. Within a
 * Race the rows run Domain, then Tier, then the Tier unlock before its own Tier's subjects,
 * then tree order — the same canonical order the URL serialises in (tech-set.ts), which the
 * picker's Domain and Tier groups then read straight off.
 *
 * It takes no Tech Set: the rows are every key the Race HAS, so a set of that Race can never
 * hold a member these rows leave out. Issue 39's silent rows were the patch over the older
 * rule that offered only stat-changing subjects, and they went with it. */
export function techOptionsFor(races: Iterable<Race>): RaceTechOptions[] {
  const asked = new Set(races);
  return RACES.filter((race) => asked.has(race)).map((race) => ({
    race,
    options: optionsOf(race),
  }));
}

// The rows are a pure function of the frozen dataset, so each Race's list is built once. Two
// readers (techRowCount, techGroupsFor) used to rebuild and re-sort all 849 rows on every
// panel open, about 2.4 ms of duplicated work (review 2026-09-08).
const OPTIONS_BY_RACE = new Map<Race, readonly TechOption[]>();

function optionsOf(race: Race): readonly TechOption[] {
  const cached = OPTIONS_BY_RACE.get(race);
  if (cached !== undefined) return cached;
  const options = [...tierOptions(race), ...subjectOptions(race)].sort(
    (a, b) =>
      RESEARCH_DOMAINS.indexOf(a.domain) - RESEARCH_DOMAINS.indexOf(b.domain) ||
      a.tier - b.tier ||
      (a.kind === "tier" ? 0 : 1) - (b.kind === "tier" ? 0 : 1) ||
      compareSlots(a, b) ||
      a.key.localeCompare(b.key),
  );
  OPTIONS_BY_RACE.set(race, options);
  return options;
}

/** The Races the Tech picker offers (ADR 0010, Issue 36): the Races on the page, narrowed to
 * the ones the Faction Selection names. This is where the site-wide selection pays — a reader
 * playing one empire faces that Race's ~39 rows rather than all four Races' 160 — and it is
 * the only thing the selection does to the Tech Set: it narrows which rows are SHOWN, never
 * which keys exist or what a tick implies (lib/tech-set.ts is untouched by it).
 *
 * Two fallbacks, each for a state the reader can actually reach, and each preferring rows that
 * mean something over an empty panel:
 *  - the page shows a Race the selection excludes — the unit search always reaches the whole
 *    Roster, so a reader playing TEC can be standing on an Advent hull — and the picker offers
 *    the page's Races, because a row whose tick moves nothing on screen would be worse;
 *  - the page shows nothing at all, because a facet on top of the selection emptied it, and
 *    the picker offers the selection's own Races rather than collapsing to no rows.
 * A selection that names no Race at all (a URL of only unrecognized tokens) on an empty page
 * is the one state that offers nothing, which is the honest answer. */
export function techPickerRaces(selection: FactionSelection, racesOnPage: Iterable<Race>): Race[] {
  const present = new Set(racesOnPage);
  const onPage = RACES.filter((race) => present.has(race));
  const selected = racesOf(selection);
  const narrowed = onPage.filter((race) => selected.includes(race));
  if (narrowed.length > 0) return narrowed;
  return onPage.length > 0 ? onPage : selected;
}

/** The Races the picker draws: the ones the surface offers, plus any Race the Tech Set
 * already holds tech for (ADR 0010 amendment, 2026-09-06). The set is the reason for the
 * second half — a reader can tick TEC research and then narrow the Faction Selection to
 * Vasari, or follow a `?tech=` link onto another Race's hull, and the members that leave the
 * panel are still counted by the trigger and still folded into the numbers. Widening here
 * keeps the one invariant that matters: every member of the set has a row, so what the
 * trigger counts is what the reader can find and untick.
 *
 * It sits between the surface's answer (techPickerRaces on a roster view, the unit's own Race
 * on an entity page or a comparison column) and the panel, so every surface holds the
 * invariant without asking for it. A set of only the shown Races' tech widens nothing, which
 * is every ordinary visit. */
export function pickerRaces(shown: readonly Race[], set: TechSet | undefined): Race[] {
  const named = racesOfTechSet(set);
  return RACES.filter((race) => shown.includes(race) || named.includes(race));
}

/** How many rows the picker draws for the Races on the page: every Tier unlock and every
 * Research Subject the Race has. It is the Tech trigger's total ("Tech: 7 of 249"), read off
 * the one list the panel is built from rather than counted a second way, so the denominator can
 * never be smaller than the count in front of it. */
export function techRowCount(races: Iterable<Race>): number {
  return techOptionsFor(races).reduce((rows, group) => rows + group.options.length, 0);
}

/** The same rows the picker actually draws: per Race, its two Domains under the Race's own
 * names, each with its five Tiers, each Tier carrying its own unlock row and the subjects that
 * sit in it. Nothing is filtered here that techOptionsFor offered — the grouping is a
 * re-shaping of the one list, so the count a test pins against the dataset is the count the
 * reader sees. */
export function techGroupsFor(races: Iterable<Race>): RaceTechGroups[] {
  return techOptionsFor(races).map(({ race, options }) => ({
    race,
    domains: RESEARCH_DOMAINS.map((domain) => ({
      domain,
      name: dataset.research.races[race][domain].name,
      tiers: dataset.research.races[race][domain].tiers.map((_, tier) => ({
        tier,
        name: tierName(race, domain, tier),
        unlock: tierOption(race, domain, tier),
        subjects: options.filter(
          (option) => option.kind === "subject" && option.domain === domain && option.tier === tier,
        ),
      })),
    })),
  }));
}
